import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

export const useDraftStream = () => {
  const [generating, setGenerating] = useState(false), [partial, setPartial] = useState(''), [error, setError] = useState('');
  const controller = useRef(null);
  useEffect(() => () => controller.current?.abort(), []);
  const stop = () => controller.current?.abort();
  const generate = async payload => {
    if (controller.current) return null;
    setGenerating(true); setPartial(''); setError('');
    const abort = new AbortController(); controller.current = abort;
    let saved = null, sawError = false;
    try {
      await api.get('/auth/me'); // Uses the existing refresh flow before opening a stream.
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ai/generate`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal: abort.signal });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Check the brief and your session, then try again.');
      }
      const reader = response.body.getReader(), decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let boundary;
        while ((boundary = buffer.indexOf('\n\n')) !== -1) {
          const packet = buffer.slice(0, boundary); buffer = buffer.slice(boundary + 2);
          const line = packet.split('\n').find(l => l.startsWith('data: '));
          if (!line) continue;
          const event = JSON.parse(line.slice(6));
          if (event.type === 'delta') setPartial(text => text + event.text);
          if (event.type === 'complete') saved = event.draft;
          if (event.type === 'error') { sawError = true; setError(event.message); }
        }
      }
      if (!saved && !sawError) throw new Error('The connection ended before the draft was saved. Check saved drafts before trying again.');
      return saved;
    } catch (e) {
      setError(e.name === 'AbortError' ? 'Generation stopped. Any completed draft will appear in saved drafts. Usage already incurred may still apply.' : (e.message || 'Claude could not finish this request.'));
      return null;
    } finally { controller.current = null; setGenerating(false); }
  };
  return { generate, generating, partial, error, setError, stop, resetPartial: setPartial };
};